<template>
  <div class="d-flex pt-2 pb-5 justify-end" ref="actionBarRef">
    <category-product-switcher
      v-if="currentCategoryId !== SCREEN.CATEGORY.ALL" />

    <strong
      v-else
      class="d-flex justify-center text-uppercase pt-4"
    >
      {{ $t('mission-planning.qst.my-questionnaires.screen.product.list.all') }}
    </strong>
    <v-spacer/>
    <category-product-filter-modal :data="data" @reset-filter="onFilterReset" />
    <bar-code-reader-modal
      v-if="isCondensedView"
      :configuration="configuration"
      :rounded="true"
      :height="actionBarButtonSize"
      :width="actionBarButtonSize"
      variant="text"
      custom-class="pa-1 mr-2"
    />
    <v-btn-toggle
      v-model="isCondensedView"
      divided
      class="d-flex reduced-height"
      @update:modelValue="changeFormView"
    >
      <v-btn
        :icon="mdiFormatListText"
        :height="actionBarButtonSize"
        variant="outlined"
        :width="actionBarButtonSize"
      ></v-btn>
      <v-btn
        :icon="mdiTable"
        :height="actionBarButtonSize"
        variant="outlined"
        :width="actionBarButtonSize"
      ></v-btn>
    </v-btn-toggle>
  </div>
  <div
    v-if="isCondensedView && filteredConfigurationProducts.length > 0"
    class="products-table" aria-labelledby="caption" tabindex="0" :style="`height: ${productTableComputedHeight}px`"
  >
    <table class="sticky-header-column">
      <thead>

      <tr>
        <th class="border-color-none table-sticky-col empty-cell-block" style="width: 400px;">
        </th>

        <template v-for="group in groupedQuestions" :key="group.id">
          <th
            v-for="(question, questionKey) in group.questions"
            :key="questionKey"
            class="bg-grey-lighten-2"
          >
            <div class="text-body-1 font-weight-bold text-center question-label-block">
              {{ question.label }} {{ question.required && '*' || '' }}
            </div>
          </th>
        </template>
      </tr>
      </thead>
      <tbody>
      <suspense>

      <v-virtual-scroll
        renderless
        :items="filteredConfigurationProducts"
        item-key="id"
        :item-height="105"
      >
        <template v-slot:default="{ item }">
          <full-product-row
            :key="item.id"
            :configuration="configuration"
            :product="item"
            :exists-group-questions-with-history="existsGroupQuestionsWithHistory"
            :grouped-questions="groupedQuestions"
            :product-selected="productSelected"
            :product-blocked-in-edition="productBlockedInEdition"
            :questions-conditions-by-product="questionsConditionsByProduct['by_product|'+item.id] || {}"
            :trigger-reload="triggerReload"
            :show-missing-mandatory-answers-flag="showMissingMandatoryAnswersFlag"
            @answeredToAQuestion="answeredToAQuestion"
            @productHasInvalidAnswer="(pid, qid) => productHasInvalidAnswer(pid, qid)"
            @updateQuestionsConditions="updateQuestionsConditions"
            @tryToChangeRowSelection="tryToChangeRowSelection"
            @triggerHistoryRecoveryProcess="triggerHistoryRecoveryProcess"
            @setRowRef="el => setRowRef(el, item.id)"
          />
        </template>
      </v-virtual-scroll>

        <template #fallback>
          <loading />
        </template>
      </Suspense>
      </tbody>
    </table>
  </div>

  <screen-by-category
    v-else-if="!isCondensedView && filteredConfigurationProducts.length > 0"
    class="mt-5"
    :screen="screen"
    :groupedQuestions="groupedQuestions"
    :attachmentLoopItems="attachmentLoopItems"
  />
  <div v-else class="d-flex flex-column justify-center align-center text-body-4 font-weight-bold my-5">
    <div><v-icon :icon="mdiMagnifyClose" /></div>
    <div>{{ $t('global.text.no-data') }}</div>
  </div>

  <v-app-bar location="bottom" ref="bottomAppBarRef" class="px-4">
    <category-product-switcher
      v-if="currentCategoryId !== SCREEN.CATEGORY.ALL" />

    <strong
      v-else
      class="d-flex justify-center text-uppercase pt-4"
    >
      {{ $t('mission-planning.qst.my-questionnaires.screen.product.list.all') }}
    </strong>
    <v-spacer />

    <v-btn
      v-if="existsGroupQuestionsWithHistory && isCondensedView"
      :prepend-icon="mdiHistory"
      :loading="globalHistoryRetrieveInProgress"
      variant="tonal"
      color="info"
      class="mx-1"
      @click="triggerHistoryRecoveryProcess()"
    >
      {{ $t('mission-planning.qst.my-questionnaires.qst.btn.history-recovery') }}
    </v-btn>

    <v-btn
      :prepend-icon="mdiArrowLeft"
      v-bind="useButtonStyle"
      @click="goToQuestionnaireCategories()"
      class="mx-1"
    >
      {{ $t('global.btn.back') }}
    </v-btn>
  </v-app-bar>
  <v-snackbar v-model="snackbar" :timeout="3000" variant="elevated">
    <div class="d-flex justify-center">
      {{
        $t('mission-planning.qst.my-questionnaires.qst.history-recovery-notification', {
          answerCount: insertedAnswersFromHistoryCount
        })
      }}
    </div>
  </v-snackbar>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';
import {computed, provide, ref, reactive, watch, onMounted, triggerRef, defineAsyncComponent, inject, onUpdated} from 'vue';
import { useElementBounding } from '@vueuse/core';
import { mdiArrowLeft, mdiHistory, mdiTable, mdiFormatListText, mdiMagnifyClose } from '@mdi/js';
import { useI18n } from 'vue-i18n';
import {
  extractScreenQuestions,
  groupedQuestionsByGroupId,
  checkIfExistsSomeGroupQuestionsWithHistory
} from '@/stores/manager/configurationManager';
import { extractReadingIdByParams } from '@/stores/manager/readingManager';
import { READING, VALUE, PDT } from '@/constants';
import { useStoreManager } from '@/stores/storeManager';
import { useApplicationStore } from '@/stores/applicationStore';
import { createReadingConditionStore } from '@/stores/conditionStore';
import CategoryProductFilterModal from '@/components/mission-planning/questionnaires/filters/CategoryProductFilterModal.vue';
import CategoryProductSwitcher from '@/components/mission-planning/questionnaires/filters/CategoryProductSwitcher.vue';
import stringHandler from '@/utils/stringHandler';
import arrayHandler from '@/utils/arrayHandler';
import {createReadingIfNotExists} from "@/stores/manager/readingManager";
import dateHandler from "@/utils/dateHandler";
import {
  saveReadingCountersDebounced,
  clearInvalidAnswers,
  getQuestionsConditionsWithAnswers} from "@/stores/manager/conditionsManager";
import { ConditionsController } from "@/utils/conditions/conditionsController";
import ScreenByCategory from "@/components/mission-planning/questionnaires/screens/ScreenByCategory.vue";
import BarCodeReaderModal from "@/components/global/BarCodeReaderModal.vue";
const FullProductRow = defineAsyncComponent(() =>
    import('@/components/mission-planning/questionnaires/parts/FullProductRow.vue')
)
import Loading from "@/components/common/Loading.vue";
import {useButtonStyle} from "@/utils/appStyleHandler";
import { extractReadingAnswersByReadingId } from '@/stores/manager/readingManager'
import questionsHandler from '@/utils/questionsHandler'
import { SCREEN } from '@/constants'
defineOptions({
  inheritAttrs: false
})

const storeManager = useStoreManager();
const applicationStore = useApplicationStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const bottomAppBarRef = ref(null);
const bottomAppBarBoundingClientRect = ref(null);
const actionBarRef = ref(null);
const actionBarBoundingClientRect = ref(null);
const snackbar = ref(false);
const isCondensedView = ref(applicationStore.data.condensedForm);
const insertedAnswersFromHistoryCount = ref(0);
const actionBarButtonSize = ref(36);
const localAnswers = ref({});
const triggerReload = ref(false);
const globalHistoryRetrieveInProgress = ref(false);
const currentProductId = ref(null);
const questionsConditions = ref({});
const productBlockedInEdition = ref({
  productId: null,
  blockingQuestionIdList: new Set()
});
const productSelected = ref(null);
const showMissingMandatoryAnswersFlag = ref(false);
const tableRows = {};
const categoryHasSwitched = ref(true);
provide('categoryHasSwitched', categoryHasSwitched);
const appIsLoading = inject('appIsLoading')

onUpdated(() => {
  setTimeout(() => {
    appIsLoading.value = false;
  }, 0)
});

const props = defineProps({
  barcode: {
    required: false
  }
});

const productTableComputedHeight = computed(() => {
  const bottomAppBarTop = bottomAppBarBoundingClientRect.value?.top ?? 0;
  const actionBarBottom = actionBarBoundingClientRect.value?.bottom ?? 0;
  const padding = 16;

  return Math.max(bottomAppBarTop - actionBarBottom - padding, 400);
});

const changeFormView = (status) => {
  applicationStore.updateCondensedForm(status);
}

const currentMissionPlanningId = computed(() => route.params.id);
provide('currentMissionPlanningId', currentMissionPlanningId);
const currentScreenId = computed(() => route.params.screen_id);
provide('currentScreenId', currentScreenId);

const screen = computed(() => {
  if (!configuration?.value?.screen) return [];

  const currentScreen = configuration.value.screen.find(
      (scr) => scr.id.toString() === currentScreenId.value
  );

  if (!currentScreen) return [];

  const conditions = createReadingConditionStore(currentReadingId.value || 'default')?.screens;

  return {
    ...currentScreen,
    visibility: conditions?.[currentScreen.id]?.visibility ?? true,
  };
});

const currentCategoryId = computed(() => route.params.category_id);
provide('currentCategoryId', currentCategoryId);
const userDetails = computed(() => applicationStore.userDetails);
provide('userDetails', userDetails);
const showHiddenQuestions = computed(() => applicationStore.showHiddenQuestions);
provide('showHiddenQuestions', showHiddenQuestions);

const missionPlanning = computed(() => {
  const id = currentMissionPlanningId.value;
  return storeManager.getFromStoreByCategory('mission', 'missionsPlannings')[id] || {};
});
provide('missionPlanning', missionPlanning);

const mission = computed(
  () =>
  storeManager.getFromStoreByCategory('mission', 'missions')[missionPlanning.value?.mission?.id] || {}
);
provide('mission', mission);

const configurationId = computed(() => mission.value?.configuration?.id);

const configuration = computed(
  () =>
  storeManager.getFromStoreByCategory('configuration', 'configurations')[configurationId.value] || {}
);
provide('configuration', configuration);

const readingsHistory = computed(() =>
  storeManager.getFromStoreByCategory('readingHistory', 'reading') || {}
);
provide('readingsHistory', readingsHistory);

const readings = computed(() =>
  storeManager.getFromStoreByCategory('reading', 'reading') || {}
);
provide('readings', readings);

const readingAnswers = computed(() =>
  storeManager.getFromStoreByCategory('reading', 'answer') || {}
);
provide('readingAnswers', readingAnswers);

const missionReadingHistory = computed(() => {
  return Object.values(readingsHistory.value).find((rh) => Number(rh.mission) === Number(missionPlanning.value?.mission?.id))
})
const missionReadingHistoryVauesByBuiltId = computed(() => {
  return Object.values(missionReadingHistory.value?.answers|| {}).reduce((acc, item) => {
    const key = !item?.related_to_type || item?.related_to_type === 'none'
      ? `${item?.question_id}`
      : `${item?.question_id}:${item?.related_to_type}|${item?.related_to_id}`;
    acc[key] = item?.value;
    return acc;
  }, {});
});
provide('missionReadingHistoryVauesByBuiltId', missionReadingHistoryVauesByBuiltId);


const attachmentLoopItems = computed(() => {
  const attachments = screen.value?.attachment?.value;

  if (!attachments) {
    return { items: [], labelKey: 'name', type: 'none' };
  }

  if (attachments === READING.ATTACHMENT_TYPE.PRODUCT) {
    const productIdsSet = new Set(
      filteredConfigurationProducts.value.map((p) => p.id?.toString())
    );
    let filteredAttachmentItems = products.value.filter((pr) =>
      productIdsSet.has(pr.id?.toString())
    );
    // filter only when is not condensed view
    if (!isCondensedView.value) {
      filteredAttachmentItems = [...filteredAttachmentItems].sort((a, b) => {
        const an = a?.name ?? '';
        const bn = b?.name ?? '';
        return an.localeCompare(bn, undefined, { sensitivity: 'base' });
      });
    }
    return { items: filteredAttachmentItems, labelKey: 'name', type: READING.ATTACHMENT_TYPE.PRODUCT };
  }

  if (attachments === READING.ATTACHMENT_TYPE.ELEMENT) {
    return { items: [], labelKey: 'name', type: READING.ATTACHMENT_TYPE.ELEMENT };
  }

  return undefined;
});
provide('attachmentLoopItems', attachmentLoopItems);
const data = reactive({
  filter: {
    search: '',
    tags: [],
    dnType: null,
    incompleteOnly: null,
    missingProductsInDN: null,
    outOfStockOnly: null
  }
})

const answers = computed(
  () =>
        Object.values(storeManager.getFromStoreByCategory('reading', 'answer'))
            .filter(answer => answer.reading?.toString() === currentReadingId.value?.toString())
        || {});

const questionsIdsPerGroup = computed(() => {
  return groupedQuestions.value.reduce((acc, group) => {
    acc[group.id] = group.questions.map(q => q.id);
    return acc;
  }, {});
});

const currentReadingId = computed(() =>
  extractReadingIdByParams(
    readings.value,
    configurationId.value,
    currentMissionPlanningId.value,
    userDetails.value?.id
  )
);

const foundAnswersPerGroup = computed(() => {
  let output = {};
  for (const [groupId, questionsIds] of Object.entries(questionsIdsPerGroup.value||{})) {
    output[groupId] = extractReadingAnswersByReadingId(
      readingAnswers.value,
       currentReadingId.value,
      questionsIds
    )
  }
  return output;
});
provide('foundAnswersPerGroup', foundAnswersPerGroup);

const screens = computed(
  () =>
    configuration.value?.screen?.filter(
      (screen) => screen.id?.toString() === currentScreenId.value?.toString()
) || []);

const screenQuestions = computed(() =>
  extractScreenQuestions(Object.values(configuration.value?.questions || {}), screens.value)
);

function countInsertedAnswers(insertedAnswers) {
  const answerKeys = Object.entries(readingAnswers.value)
          .filter( ([key, answer]) =>
        answer.reading?.toString() === currentReadingId.value?.toString() &&
        insertedAnswers.includes(key)
          )
      || {}
  return answerKeys.length;
}

const groupedQuestions = computed(() =>
  groupedQuestionsByGroupId(screenQuestions.value, currentReadingId.value || VALUE.DEFAULT)
);

const pointsOfSale = computed(() =>
    storeManager.getFromStoreByCategory('mission', 'pointsOfSale') || {}
);

const pointOfSale = computed(() => {
  const pos = pointsOfSale?.value[mission.value?.pos?.id] || null
  if (pos === null) {return {};}
  pos.id = mission.value?.pos?.id
  return pos
})
provide('currentPointOfSale', pointOfSale);

const dnProductIndicators = computed(() =>
    pointOfSale.value?.productIndicators?.dn
);

const dnoProductIndicators = computed(() =>
    pointOfSale.value?.productIndicators?.dno
);

const products = computed(() => Object.values(configuration.value?.catalog?.product || []).map(product => {
  const productIndicatorType =
      dnoProductIndicators.value?.includes(product?.id?.toString()) ? PDT.TYPE.DNO :
          (dnProductIndicators.value?.includes(product?.id?.toString()) ? PDT.TYPE.DN : '');
        return {
          ...product,
          productIndicatorType: productIndicatorType
        }
}).filter(product => {
        if (dnProductIndicators.value && dnProductIndicators.value.length > 0) {
          return dnProductIndicators.value.includes(product?.id?.toString())
  }
  else return true;
}) || {});

const unAnsweredMandatoryQuestionsProductIds = computed(() => {
  return new Set(
    questionsConditionsWithAnswers.value
      .filter((question) => !question.hasAnswer && question.mandatory && question.visibility && question.code.includes('by_product'))
      .map((question) => question.code.split('|')[1])
  );
});

const missingDnDnoProductIds = computed(() => {
    const presenceTypeIds = new Set(
      screenQuestions.value
        .filter((question) => question.isPresenceType === true)
        .map((question) => question.id.toString())
    );
    const filteredQuestions = questionsConditionsWithAnswers.value
      .filter((question) =>
        presenceTypeIds.has(question.question_id.toString()) &&
        question.hasAnswer === true &&
        question.hasTrueAnswer === false
      );
    return new Set(
      filteredQuestions.map((question) => question.code.split('|').pop())
    );
  }
)

const outOfStockProductIds = computed(() => {
    const outOfStockTypeIds = new Set(
      screenQuestions.value
        .filter((question) => question.isOutOfStockType === true)
        .map((question) => question.id.toString())
    );
    const filteredQuestions = questionsConditionsWithAnswers.value
      .filter((question) =>
        outOfStockTypeIds.has(question.question_id.toString()) &&
        question.hasAnswer === true &&
        question.hasTrueAnswer === true
      );
    return new Set(
      filteredQuestions.map((question) => question.code.split('|').pop())
    );
  }
)

const configurationProducts = computed(() => {
  let outputProducts =
      products.value.filter(
          (p) => currentCategoryId.value?.toString() === SCREEN.CATEGORY.ALL || p?.category?.id?.toString() === currentCategoryId.value?.toString()
        ) || []

  if (data.filter.incompleteOnly) {
    outputProducts = outputProducts.filter((product) =>
      unAnsweredMandatoryQuestionsProductIds.value.has(product.id?.toString()) || productSelected.value === product.id
    )
  }
  if (data.filter.missingProductsInDN) {
    outputProducts = outputProducts.filter((product) =>
      missingDnDnoProductIds.value.has(product.id?.toString()) || productSelected.value === product.id
    );
  }
  if (data.filter.outOfStockOnly) {
    outputProducts = outputProducts.filter((product) =>
      outOfStockProductIds.value.has(product.id?.toString()) || productSelected.value === product.id
    );
  }
  return outputProducts
})

const filterConfigurationProducts = () => {
  return configurationProducts.value
    .filter(p => (
        data.filter.search === '' || data.filter.search === null || stringHandler.substringFoundIn(data.filter.search, [p.name, p.ean, p.brand?.name])
      ) && (
        data.filter.tags.length === 0 || arrayHandler.hasCommonElement(p.tags, data.filter.tags)
      ) && (
        data.filter.dnType === null || p.productIndicatorType === data.filter.dnType
  )
    );
};

const filterIsSet = computed(() =>
  data.filter.search !== '' ||
  data.filter.tags.length !== 0 ||
  data.filter.dnType !== null ||
  data.filter.incompleteOnly ||
  data.filter.missingProductsInDN
)
provide('filterIsSet', filterIsSet);

const sortBy = (object) =>
  Object.values(object).sort((a, b) => {
    return a.brand.name?.localeCompare(b.brand.name) || a.name?.localeCompare(b.name);
});

const sortedConfigurationProducts = computed(() =>
  sortBy(configurationProducts.value)
);

const sortedFilteredProducts = computed(() =>
  sortBy(filterConfigurationProducts())
);

const filteredConfigurationProducts = computed(() =>
  filterIsSet.value ? sortedFilteredProducts.value : sortedConfigurationProducts.value
);

const existsGroupQuestionsWithHistory = computed(() =>
  checkIfExistsSomeGroupQuestionsWithHistory(
    groupedQuestions.value,
    missionReadingHistory.value,
    READING.ATTACHMENT_TYPE.PRODUCT,
    filteredConfigurationProducts.value
  )
)


const goToQuestionnaireCategories = () => {
  router.push({
    name: 'mission-planning-questionnaire-screen',
    params: {
      id: currentMissionPlanningId.value,
      screen_id: currentScreenId.value
    },
  });
}

const resetQueryParams = () => {
  router.push({
    name: 'mission-planning-questionnaire-screen-category',
    params: {
      screen_id: currentScreenId.value,
      category_id: currentCategoryId.value,
      id: currentMissionPlanningId.value
    }
  });
}

const onFilterReset = () => data.filter = {
    search: '',
    tags: [],
    dnType: null,
    incompleteOnly: null,
    missingProductsInDN: null,
    outOfStockOnly: null
  };

function updateConditions(readingId) {
  let initialContext = {
    currentValue: null,
    answers: answers.value,
    pos: pointOfSale.value,
    attachementType: 'none',
    attachementId: null,
    prod: null,
    nowDatetime: new Date()
  }
  const rawConfigurations = storeManager.getFromStoreByCategory('configuration', 'configurations')
  const { questions } = new ConditionsController(rawConfigurations[configurationId.value], initialContext, readingId).createConditions();
  saveReadingCountersDebounced(readingId, rawConfigurations[configurationId.value])

  return questions;
}

function triggerFormsReload() {
  triggerReload.value = !triggerReload.value;
}

const recursiveClearInvalidAnswers = async (readingId) => {
  let cleared = true;
  let questionsResult;
  while (cleared) {
    questionsResult = updateConditions(readingId);
    cleared = await clearInvalidAnswers(readingId);
  }
  return questionsResult;
}

const triggerHistoryRecoveryProcess = async (productAttachmentId = null) => {
  globalHistoryRetrieveInProgress.value = true;
  insertedAnswersFromHistoryCount.value = 0;
  const answersHistory = missionReadingHistory.value?.answers;
  if (!answersHistory) {
    globalHistoryRetrieveInProgress.value = false;
    return;
  }
  const productIds = productAttachmentId === null ?
      filteredConfigurationProducts.value.map((p) => p.id?.toString()) :
      [productAttachmentId?.toString()];

  const productIdSet = new Set(productIds);

  const filteredProductsAnswersHistory = Object.values(answersHistory).filter(
    (ah) =>
      ah.related_to_type === READING.ATTACHMENT_TYPE.PRODUCT &&
      productIdSet.has(ah.related_to_id?.toString())
  );

  if (!currentReadingId.value || currentReadingId.value === VALUE.DEFAULT) {
    await createReadingIfNotExists(
      currentReadingId.value,
      configurationId.value,
      currentMissionPlanningId.value || null,
      userDetails.value.id,
      configuration.value?.data || null
    );
  }
  const screenQuestionsMap = new Map(
    screenQuestions.value.map(q => [q.id?.toString(), q])
  );

  const existingAnswerKeys = new Set(
    answers.value.map(a => questionsHandler.buildId(a.question, a.attachment_type, a.attachment_id))
  );

  const insertions = filteredProductsAnswersHistory.map(async (answerHistory) => {
    const questionId = answerHistory.question_id?.toString();
    const relatedId = answerHistory.related_to_id?.toString();
    const type = answerHistory.related_to_type;

    const key = questionsHandler.buildId(questionId, type, relatedId);
    const question = screenQuestionsMap.get(questionId);

    if (!question || !question.history || existingAnswerKeys.has(key)) {
      return null;
    }

    return await addNewReadingAnswer(
      questionId,
      currentReadingId.value,
      answerHistory.value,
      type,
      relatedId
    );
  });

  const insertedAnswersRaw = await Promise.all(insertions);
  const insertedAnswers = insertedAnswersRaw.filter(Boolean);

  const questionsConditionsResult = await recursiveClearInvalidAnswers(currentReadingId.value);

  updateQuestionsConditions(questionsConditionsResult);
  triggerFormsReload();

  insertedAnswersFromHistoryCount.value = countInsertedAnswers(insertedAnswers);

  if (insertedAnswersFromHistoryCount.value > 0) snackbar.value = true;
  globalHistoryRetrieveInProgress.value = false;
}
async function addNewReadingAnswer(
  questionId,
  readingId,
  answerValue,
  attachmentType,
  attachmentId
) {
  const creationDate = dateHandler.generateNowUnixTimestamp();
  const readingAnswer = {
    reading: readingId,
    question: parseInt(questionId),
    value: answerValue,
    attachment_type: attachmentType,
    attachment_id: attachmentId,
    creation_date: creationDate
  };
  return await storeManager.add('reading', 'answer', readingAnswer);
}
const answeredToAQuestion = ({attachmentId, answers}) => {
  currentProductId.value = attachmentId;
  localAnswers.value = answers;
};

const productHasInvalidAnswer = (productId, targetQuestionId = null) => {
  let blockingQuestionIdList = productBlockedInEdition.value?.blockingQuestionIdList || new Set();
  if (targetQuestionId) {
    blockingQuestionIdList.add(targetQuestionId.toString());
  }
  productBlockedInEdition.value = {productId: productId, blockingQuestionIdList: blockingQuestionIdList};
}

function groupByFullSection(data) {
  const grouped = { default: {} };

  Object.entries(data).forEach(([key, value]) => {
    const match = key.match(/:(by_product\|\d+)$/); // Extract "by_product|xxx" if exists

    if (match) {
      const section = match[1]; // "by_product|xxx"
      if (!grouped[section]) {
        grouped[section] = {};
      }
      grouped[section][key] = value;
    } else {
      grouped.default[key] = value;
    }
  });

  return grouped;
}
const questionsConditionsByProduct = computed(() => {
  return groupByFullSection(questionsConditions.value);
});
const updateQuestionsConditions = (qc) => {
  questionsConditions.value = qc;
}

const barcodeSearch = (barcode) => {
  data.filter = {
    search: barcode,
    tags: [],
    dnType: null,
    incompleteOnly: null,
    missingProductsInDN: null,
    outOfStockOnly: null
  };
}
const setRowRef = (el, productId) => {
  if (el) {
    tableRows[productId]= ref(el)
  }
};

function scrollToRow(productId, callback) {
  const row = tableRows[productId]?.value;
  if (!row) return;

  let lastPosition = null;
  let framesWithoutChange = 0;

  function checkIfScrolled() {
    const currentPosition = row.getBoundingClientRect().top;

    if (lastPosition !== null && currentPosition === lastPosition) {
      framesWithoutChange++;
    } else {
      framesWithoutChange = 0;
    }

    lastPosition = currentPosition;

    if (framesWithoutChange > 5) {
      callback?.();
      return;
    }

    requestAnimationFrame(checkIfScrolled);
  }

  row.scrollIntoView({ behavior: "smooth", block: "center" });
  requestAnimationFrame(checkIfScrolled);
}
function tryToChangeRowSelection(productId) {
  if (productBlockedInEdition.value?.productId) {
    if (productBlockedInEdition.value?.productId?.toString() !== productId?.toString()) {
      scrollToRow(productBlockedInEdition.value?.productId, () => {
        showMissingMandatoryAnswersFlag.value = !showMissingMandatoryAnswersFlag.value;
      });
    }
    return;
  }
  productSelected.value = productId;
}

watch(
  () => props.barcode,
  (barcode) => {
    if (barcode) barcodeSearch(barcode);
  }
)

watch(data, (newFilter) => {
  resetQueryParams()
}, {deep: true});

const questionsConditionsWithAnswers = computed(() => getQuestionsConditionsWithAnswers(currentReadingId.value, Object.values(localAnswers.value), questionsConditions.value));

watch(() => answers.value, (a) => {
  localAnswers.value = a;
}, {immediate: true, deep: true});

onMounted(() => {
  if (props.barcode) barcodeSearch(props.barcode);
  watch(() => currentReadingId.value, () => {
    updateQuestionsConditions(createReadingConditionStore(currentReadingId.value || VALUE.DEFAULT)?.questions || {});
  }, {immediate: true});

  // see EDIT section of
  // https://www.reddit.com/r/sveltejs/comments/17yjzvy/help_getboundingclientrect_wrong/?utm_source=share&utm_medium=web3x&utm_name=web3xcss&utm_term=1&utm_content=share_button
  // Don't know why it works inside the callback of an empty timeout, but not outside...
  setTimeout(() => {
    bottomAppBarBoundingClientRect.value = useElementBounding(bottomAppBarRef)
    actionBarBoundingClientRect.value = useElementBounding(actionBarRef)
  }, 0);
})
</script>

<style scoped>
.question-label-block {
  white-space: initial;
  z-index: 3;
}

.empty-cell-block {
  min-width: 100px;
  max-width: 370px;
  left: 0;
  z-index: 1;
}

.v-btn-group--density-default.v-btn-group.reduced-height {
  height: 36px;
}
.text-body-1 {
  font-size: 0.75rem !important;
}
.v-card-title {
  font-size: 0.75rem !important;
  padding: 0.3rem 0.5rem;
}
.v-card-text {
  font-size: 0.75rem !important;
}
.v-table--density-default {
  --v-table-header-height: 30px;
  --v-table-row-height: 52px;
}
.v-chip.v-chip--density-default {
  font-size: 0.75rem;
}
.v-chip.v-chip--size-small {
  --v-chip-size: 0.75rem;
  --v-chip-height: 20px;
  font-size: 0.75rem;
  padding: 0 5px;
}


table.sticky-header-column {
  white-space: nowrap;
  margin: 0;
  border: 0 none;
  border-collapse: separate;
  border-spacing: 0;
  width: 100%;
}
table.sticky-header-column td,
table.sticky-header-column th {
  padding: 0 16px;
  border: 1px solid #D1D1D1;
}

table.sticky-header-column thead th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: white;
  height: 30px;
}
table.sticky-header-column td {
  background: #fff;
  padding: 4px 5px;
  text-align: center;
}


table.sticky-header-column tbody th {
  font-weight: 100;
  font-style: italic;
  text-align: left;
  position: relative;
}
table.sticky-header-column thead th:first-child {
  position: sticky;
  left: 0;
  z-index: 2;
}
table.sticky-header-column tbody th {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

div.products-table {
  overflow: auto;
  border-radius: inherit;
  flex: 1 1 auto;
  display: flex;
  align-items: flex-start;
}
div.products-table:focus {
  outline: 0;
}

</style>